import React from "react";
import { Row, Col } from "antd";
import {
  ObjectFieldTemplateProps,
  descriptionId,
  getTemplate,
  getUiOptions,
} from "@rjsf/utils";
import styles from "./MaatregelenRowTemplate.module.css";

/** 
 * Template for rendering individual measure items in a 3-column row layout:
 * Column 1: maatregel (measure question)
 * Column 2: all indicator fields grouped together
 * Column 3: toelichting (explanation)
 */
export default function MaatregelenRowTemplate({
  description,
  properties,
  uiSchema,
  idSchema,
  formData,
  schema,
  registry,
}: ObjectFieldTemplateProps) {
  const uiOptions = getUiOptions(uiSchema);

  // Check if this is the first item in the array
  const arrayIndex = idSchema.$id ? parseInt(idSchema.$id.split('_').pop() || '0') : 0;
  const isFirstRow = arrayIndex === 0;

  const DescriptionFieldTemplate = getTemplate(
    "DescriptionFieldTemplate",
    registry,
    uiOptions
  );

  // Helper function to get element by name
  const getElementByKey = (key: string) =>
    properties.find((element) => element.name === key);

  // Group properties by type
  const maatregelElement = getElementByKey("maatregel");
  const toelichtingElement = getElementByKey("toelichting");
  const belemmeringsElement = getElementByKey("belemmering");
  const measureAnswersElement = getElementByKey("measure_answers");
  
  // Get all indicator elements (both checkboxes and scores)
  const indicatorElements = properties.filter((element) => 
    element.name.startsWith("indicator") && !element.name.endsWith("_score")
  );
  
  // Get all score elements, but only show those where the corresponding indicator is true
  const scoreElements = properties.filter((element) => {
    if (!element.name.endsWith("_score")) return false;
    
    // Extract the indicator name by removing "_score" suffix
    const indicatorName = element.name.replace("_score", "");
    
    // Check if the corresponding indicator is true in formData
    return formData && formData[indicatorName] === true;
  });

  return (
    <>
      {description && (
        <DescriptionFieldTemplate
          id={descriptionId(idSchema)}
          description={description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}

      {/* Column Headers - only show for first row - different headers dependent on view of page */}
      {isFirstRow && (
        <Row gutter={16} className={`mb-2 font-semibold text-gray-700 border-b pb-2 ${styles.headerRow}`}>
          <Col span={8} className="pr-3">
            Maatregel
          </Col>
          <Col span={9} className="px-3">
            <span className="overviewMode-header">Indicatoren</span>
            <span className="detailMode-header">{measureAnswersElement ? "Verdiepende vragen" : "Scores"}</span>
          </Col>
          <Col span={7} className="pl-3">
            <span className="overviewMode-header">Toelichting (optioneel)</span>
            <span className="detailMode-header">{measureAnswersElement ? "" : "Belemmering (optioneel)"}</span>
          </Col>
        </Row>
      )}
      
      {measureAnswersElement && (
        <div className={styles.measureAnswersDetailRow}>
          <Row gutter={16} className={`py-4 ${styles.rowContainer}`}>
            <Col span={8} className="pr-3">
              {maatregelElement && (
                <div className="w-full maatregelen-measure">
                  {maatregelElement.content}
                </div>
              )}
            </Col>
            <Col span={16} className="px-3">
              {measureAnswersElement.content}
            </Col>
          </Row>
        </div>
      )}

      <div className={measureAnswersElement ? styles.measureAnswersOverviewRow : undefined}>
      <Row gutter={16} className={`py-4 ${styles.rowContainer}`}>
        {/* Column 1: Maatregel */}
        <Col span={8} className="pr-3">
          {maatregelElement && (
            <div className={`w-full  maatregelen-measure `}>
              {maatregelElement.content}
            </div>
          )}
        </Col>

        {/* Column 2: Indicators */}
        <Col span={9} className="px-3">
          <div className="flex flex-col gap-2">
            {/* Indicators displayed horizontally */}
            <div className={`flex flex-row gap-3 items-end flex-wrap maatregelen-indicators`}>
              {indicatorElements.map((element, index) => (
                <div key={`indicator-${index}`} className="flex-none maatregelen-indicator">
                  {element.content}
                </div>
              ))}
            </div>
            
            {/* Show scores in detail mode when measure questions are unavailable */}
            {!measureAnswersElement && (
              <div className={`maatregelen-scores`}>
                {scoreElements.map((element, index) => (
                  <div key={`score-${index}`} className="mb-2 maatregelen-score">
                    {element.content}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>

        {/* Column 3: Toelichting */}
        <Col span={7} className="pl-3">
          {toelichtingElement && (
            <div className={`w-full maatregelen-toelichting`}>
              {toelichtingElement.content}
            </div>
          )}
           {/* Show belemmering in detail mode */}
            {!measureAnswersElement && belemmeringsElement && (
              <div className={`maatregelen-belemmering`}>
                {belemmeringsElement.content}
              </div>
            )}
        </Col>
      </Row>
      </div>
    </>
  );
}